'use client';

import { useMemo, useState } from 'react';
import { SiteHeader } from '@/app/components/layout/SiteHeader';
import PriceAnalyticsPanel from '../components/analytics/PriceAnalyticsPanel';

const EXAMPLES = [
  { label: 'N1 (Islington) — Terraced', postcode: 'N1 1PN', type: 'terraced', price: 875000 },
  { label: 'SW18 (Wandsworth) — Flat', postcode: 'SW18 1TW', type: 'flat', price: 520000 },
  { label: 'SE21 (Dulwich) — Semi-detached', postcode: 'SE21 8AL', type: 'semi-detached', price: 1150000 },
  { label: 'E2 (Shoreditch) — Flat', postcode: 'E2 7DJ', type: 'flat', price: 390000 },
  { label: 'BS8 (Bristol Clifton) — Detached', postcode: 'BS8 1AB', type: 'detached', price: 650000 },
  { label: 'SW1W (Belgravia) — Flat', postcode: 'SW1W 0RB', type: 'flat', price: 1200000 },
];

const inputClass =
  'w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#0f0f0d] placeholder:text-[#b8b5ae] focus:border-[#2d7a5f] focus:outline-none focus:ring-1 focus:ring-[#d4ede5]';

export default function AnalyticsPage() {
  const [postcode, setPostcode] = useState('N1 1PN');
  const [propertyType, setPropertyType] = useState('terraced');
  const [askingPrice, setAskingPrice] = useState(875000);
  const [key, setKey] = useState(0);

  const mapReportHref = useMemo(() => {
    const t = postcode.trim();
    return t ? `/map?postcode=${encodeURIComponent(t)}` : '/map';
  }, [postcode]);

  const handleExample = (ex: (typeof EXAMPLES)[0]) => {
    setPostcode(ex.postcode);
    setPropertyType(ex.type);
    setAskingPrice(ex.price);
    setKey((k) => k + 1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setKey((k) => k + 1);
  };

  return (
    <main className="min-h-screen bg-[#faf8f4] text-[#0f0f0d]">
      <SiteHeader mapReportHref={mapReportHref} />

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2d7a5f]">Market trends</p>
          <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
            Property price analytics
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#7a7870]">
            HM Land Registry data (1995–2026) — 2,308 UK postcode districts
          </p>
        </header>

        <div className="mb-6 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#7a7870]">Quick examples</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.postcode}
                type="button"
                onClick={() => handleExample(ex)}
                className="rounded-full border border-black/10 bg-[#f3f0ea] px-3 py-1 text-xs text-[#7a7870] transition hover:border-[#d4ede5] hover:bg-[#d4ede5] hover:text-[#1a4a3a]"
              >
                {ex.label}
              </button>
            ))}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mb-8 flex flex-wrap items-end gap-3 rounded-2xl border border-black/10 bg-white p-4 shadow-sm"
        >
          <div className="min-w-[140px] flex-1">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#7a7870]">
              Postcode
            </label>
            <input value={postcode} onChange={(e) => setPostcode(e.target.value)} className={inputClass} placeholder="e.g. N1 1PN" />
          </div>
          <div className="w-40">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#7a7870]">
              Property type
            </label>
            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className={inputClass}>
              <option value="detached">Detached</option>
              <option value="semi-detached">Semi-detached</option>
              <option value="terraced">Terraced</option>
              <option value="flat">Flat</option>
            </select>
          </div>
          <div className="w-32">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#7a7870]">
              Asking price (£)
            </label>
            <input
              type="number"
              value={askingPrice}
              onChange={(e) => setAskingPrice(Number(e.target.value))}
              className={inputClass}
            />
          </div>
          <button
            type="submit"
            className="rounded-xl bg-[#1a4a3a] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#2d7a5f]"
          >
            Analyse
          </button>
        </form>

        <section className="rounded-3xl border border-black/10 bg-white p-5 shadow-sm sm:p-8">
          <PriceAnalyticsPanel key={key} postcode={postcode} propertyType={propertyType} askingPrice={askingPrice} />
        </section>
      </div>
    </main>
  );
}
